import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'redux-form';
import { FormText } from 'reactstrap';
import Allocation from 'shared/allocation/materialAllocation';
import Note from 'modules/modals/common/note';
import CategoryTemplateItem from 'shared/categoryTemplateItem';
import Approvers from 'modules/request/components/approvers';
import { useAppKeyWords, useLoadProjectsAndResources } from 'hooks';
import { useSelector } from 'react-redux';
import { FORM_NAME } from './modal';
import { selectCompanyApprovers } from '../../../../selectors/company';
import {
    DatePickerReduxFormWrapper,
    Divider,
    Layout,
    NumberInput,
    SelectReduxFormWrapper,
    TextFieldReduxFormWrapper,
    Tooltip,
    Typography,
} from '@hub-mono/ui';
import { BookingRepeatFormPart, makeSelectPermittedResourcesForRequest, makeSelectPermittedProjectsForScheduleRequest } from '@hub-mono/app-modules';
import { normalizeDate } from '../../../../utils/formUtil';
import { isBefore } from '@hub-mono/utils';
import { ColorBlockV2 } from '../../../../shared/categoryTemplateItem/styles';
import styled from 'styled-components';
import { useDayCounterLogic } from '../../../modals/common/useDayCounterLogic';
import { makeProjectByIdFromMap } from "../../../../selectors/project";

const StyledTooltip = styled(Tooltip)`
    &.hub-ui__tooltip-content-wrapper {
        flex: 0 0 85px;
    }
`;

const StyledNumberInput = styled(NumberInput)`
    flex: 0 0 85px;
`;

const RequestModalBody = props => {
    const {
        handleSubmit,
        defaultApprovers,
        onCategoryChange,
        onChange,
        categoryTemplates: categoryTemplatesProp,
        formValues,
        booking,
        vacationId,
    } = props;

    const categoryTemplates = useMemo(() => {
        return categoryTemplatesProp.sort((a, b) => {
            if (a.groupName < b.groupName) {
                return -1;
            }
            if (a.groupName > b.groupName) {
                return 1;
            }
            return 0;
        });
    }, [categoryTemplatesProp]);

    useLoadProjectsAndResources();
    const { state, startDate, notifyPM, approvers: selectedApprovers, project, resource } = formValues;

    const projectSelector = useMemo(() => {
        return makeProjectByIdFromMap(project?._id);
    }, [project?._id]);
    const storeProject = useSelector(projectSelector)

    const { resourceKeyWord, resourcePluralKeyWord, projectKeyWord } = useAppKeyWords();
    const approvers = useSelector(selectCompanyApprovers);
    const projectsSelector = useSelector(() => {
        return makeSelectPermittedProjectsForScheduleRequest({resource})
    })
    const projects = useSelector(projectsSelector);
    const resourcesSelector = useMemo(() => {
        return makeSelectPermittedResourcesForRequest({ project: storeProject || project }); // project which is used from booking don't have assigned resources
    }, [project, storeProject]);
    const resources = useSelector(resourcesSelector);

    const isVacation = project?._id === vacationId;

    const availableProjects = useMemo(() => {
        if (projects.some(p => p._id === booking?.project._id)) {
            return projects;
        }

        return [...projects, ...(booking?.project ? [booking.project] : [])];
    }, [projects, booking?.project]);

    const availableResources = useMemo(() => {
        if (resources.some(p => p._id === booking?.resourceInfo._id)) {
            return resources;
        }

        return [...resources, ...(booking?.resourceInfo ? [booking.resourceInfo] : [])];
    }, [resources, booking?.resourceInfo]);

    const noOfSelectedResource = formValues?.resource?._id ? 1 : 0;
    const {
        handleDaysCounterChange,
        handleStartDateChange,
        daysCounter,
        handleEndDateChange,
        handleDaysCounterBlur,
    } = useDayCounterLogic({
        formName: FORM_NAME,
        fieldName: 'endDate',
        startDate: formValues?.startDate,
        endDate: formValues?.endDate,
        resourceId: formValues?.resource?._id,
        initialized: formValues?.initialized,
    });

    return (
        <Form className="needs-validation" onSubmit={handleSubmit}>
            <Layout stack gap={24}>
                <Layout stack gap="--Spacing-100">
                    <Typography variant="label" size="large" prominent>
                        Select {resourceKeyWord}:
                    </Typography>
                    <Field
                        name="resource"
                        disableClearable
                        component={SelectReduxFormWrapper}
                        optionValue="_id"
                        optionLabel="name"
                        options={availableResources}
                    />
                    <FormText className="required">
                        Choose which {resourcePluralKeyWord} you would like to request
                    </FormText>
                </Layout>
                <Divider />
                <Layout stack gap="--Spacing-100">
                    <Typography variant="label" size="large" prominent>
                        Select a {projectKeyWord} or Event:
                    </Typography>
                    <Field
                        name="project"
                        component={SelectReduxFormWrapper}
                        optionValue="_id"
                        optionLabel="name"
                        options={availableProjects}
                        disableClearable
                    />
                    <FormText className="required">
                        Choose a Project or Event that you would like to request the {resourceKeyWord}
                        to be scheduled on
                    </FormText>
                </Layout>
                <Divider />
                <Layout stack gap="--Spacing-100">
                    <Typography variant="label" size="large" prominent>
                        Booking Title:
                    </Typography>
                    <Field name="title" icon="fa-font" component={TextFieldReduxFormWrapper} />
                    <FormText>
                        Give your request a title. If blank then the {projectKeyWord} name is displayed on request by
                        default.
                    </FormText>
                </Layout>
                <Divider />
                <Layout stack gap="--Spacing-100">
                    <Typography variant="label" size="large" prominent>
                        Requested date:
                    </Typography>
                    <Layout gap="--Spacing-200">
                        <Field
                            name="startDate"
                            label="Start Date"
                            component={DatePickerReduxFormWrapper}
                            normalize={normalizeDate}
                            dataCy="request-modal-start-date"
                            onChange={handleStartDateChange}
                        />
                        <Field
                            name="endDate"
                            label="End Date"
                            component={DatePickerReduxFormWrapper}
                            normalize={normalizeDate}
                            dataCy="request-modal-end-date"
                            onChange={handleEndDateChange}
                            disabledDays={date => {
                                if (!startDate) {
                                    return false;
                                }
                                return isBefore(date, startDate, 'day');
                            }}
                        />
                        <StyledTooltip
                            title={
                                noOfSelectedResource === 0
                                    ? `Select a ${resourceKeyWord} to enable this field`
                                    : `Disabled due to selected ${resourcePluralKeyWord} having availability conflicts.`
                            }
                            disabled={noOfSelectedResource === 1}
                            placement="top"
                        >
                            <StyledNumberInput
                                disabled={noOfSelectedResource !== 1}
                                dataCy="booking-modal-days-counter"
                                name="daysCounter"
                                label="Days"
                                value={daysCounter}
                                onChange={handleDaysCounterChange}
                                onBlur={handleDaysCounterBlur}
                                withSpinButtons
                                disableSpecialChars
                                step={1}
                                min={1}
                                max={1000}
                            />
                        </StyledTooltip>
                    </Layout>
                </Layout>
                <Divider />
                <BookingRepeatFormPart formName={FORM_NAME} />
                <Divider />
                <Approvers
                    defaultApprovers={defaultApprovers}
                    approvers={approvers}
                    onChange={onChange}
                    notifyPM={notifyPM}
                    selectedApprovers={selectedApprovers}
                    isVacation={isVacation}
                />
                <Divider />
                <Note
                    title="Request Note:"
                    placeholder="Add an optional note here..."
                    formText="An additional message or note can be added here which will be sent along with the request."
                />
                <Divider />
                <Allocation
                    title="Requested Booking Allocation:"
                    subtitle="Select how much allocation you want to request"
                    formText={`The booking allocation amount per ${resourceKeyWord}`}
                    allocationDefaultState={state}
                    onChange={onChange}
                    formName={FORM_NAME}
                />
                <Divider />
                <Layout stack gap="--Spacing-100">
                    <Typography variant="label" size="large" prominent>
                        Booking Category:
                    </Typography>
                    <Field
                        name="category"
                        disableClearable
                        component={SelectReduxFormWrapper}
                        optionValue="_id"
                        optionLabel={item => {
                            return `${item?.name || ''}${item.allocationText ? ` - ${item.allocationText}` : ''}`;
                        }}
                        groupBy={item => item.groupName}
                        onChange={onCategoryChange}
                        labelRenderer={item => <CategoryTemplateItem item={item} />}
                        renderTags={item => <CategoryTemplateItem item={item} />}
                        options={categoryTemplates}
                        StartAdornment={({ value }) => {
                            return <ColorBlockV2 color={value?.gridColor} />;
                        }}
                    />
                    <FormText>Choose a booking category from the options above</FormText>
                </Layout>
            </Layout>
        </Form>
    );
};

RequestModalBody.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    defaultApprovers: PropTypes.array.isRequired,
    onCategoryChange: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    categoryTemplates: PropTypes.array.isRequired,
};

export default RequestModalBody;
